/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { visitOptions } from '@brands/Dashboard/MedicalHistory/DentalHistory/utils/questions';
import { DentalHistorySubmit } from '@brands/Dashboard/MedicalHistory/DentalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { DentalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { getSecondary } from '@brands/Utils/getPrimaryColor';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button, Checkbox } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Container from '../../../../../Components/Container/Container';
import TextField from '../../../../../Components/Inputs/MuiTextField/TextField';
import {
  routeAfterClinicalData,
  useMedicalHistoryOptional,
} from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import styles from './styles.module.scss';

const createDentalHistoryValidationSchema = (fieldsOptional: boolean): yup.AnyObjectSchema =>
  yup.object({
    user_id: yup.number().optional(),

    last_dental_visit: yup
      .string()
      .optional()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? undefined : value))
      .test('required-when-needed', 'Last dental visit is required', (value) => fieldsOptional || Boolean(value))
      .test('valid-or-dontknow', 'Please enter a valid date or select "Not sure"', (value) => {
        if (!value) return true;
        if (value === 'dontKnow') return true;

        const date = new Date(value);
        return !isNaN(date.getTime());
      })
      .test('not-in-future', "You can't enter a future date", (value) => {
        if (!value || value === 'dontKnow') return true;

        const enteredDate = new Date(value);
        const now = new Date();
        return enteredDate <= now;
      }),

    who_did_you_see: yup
      .string()
      .optional()
      .transform((value, originalValue) => (originalValue?.trim() === '' ? undefined : value))
      .test('required-when-needed', 'Dentist seen is required', (value) => fieldsOptional || Boolean(value))
      .test('valid-or-dontknow', 'Please enter a dentist name or select "Not sure"', (value) => {
        if (!value) return true;
        if (value === 'dontKnow') return true;
        return value.trim().length > 0;
      })
      .max(100, 'Dentist name is too long'),

    visit_type: yup.string().optional(),
  });

const ClinicalDataStepNine = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const bypassRequired = useMedicalHistoryOptional(values);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<DentalHistorySubmit>({
    last_dental_visit: '',
    who_did_you_see: '',
    visit_type: '',
  });
  const [savedLastDentalVisit, setSavedLastDentalVisit] = useState('');
  const [savedWhoDidYouSee, setSavedWhoDidYouSee] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { state, pathname } = useLocation();
  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data/end-step') || false;
  }, [state]);
  const dentalHistoryValidationSchema = useMemo(
    () => createDentalHistoryValidationSchema(bypassRequired),
    [bypassRequired],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    clearErrors,
    getValues,
    reset,
  } = useForm<DentalHistorySubmit>({
    mode: 'onChange', // already present
    resolver: yupResolver(dentalHistoryValidationSchema),
    defaultValues: initialValues,
  });

  const saveFormData = useCallback((): void => {
    if (hasUnsavedChanges && !loading) {
      const currentValues = {
        ...getValues(),
        id: objectId,
      };
      sessionStorage.setItem('dentalHistory', JSON.stringify(currentValues));
    }
  }, [hasUnsavedChanges, loading, getValues, objectId]);

  const goToNextStep = (): void => {
    roleNavigate(routeAfterClinicalData(values));
  };

  const onSubmit = async (data: DentalHistorySubmit): Promise<void | null> => {
    setLoading(true);
    setHasUnsavedChanges(false);
    try {
      const currentUserId = selectedPatientId as number;

      const updatedData = {
        ...data,
        user_id: currentUserId,
      };

      const hasAnswer = Boolean(data.last_dental_visit) || Boolean(data.who_did_you_see) || Boolean(data.visit_type);
      let persisted = false;

      if (objectId) {
        await updateClinicalData('dental_history', objectId, updatedData);
        persisted = true;
      } else if (hasAnswer) {
        await addClinicalData('dental_history', updatedData);
        persisted = true;
      }

      const snapshot = getValues();
      setInitialValues(snapshot);
      reset(snapshot);
      setSavedLastDentalVisit('');
      setSavedWhoDidYouSee('');
      sessionStorage.removeItem('dentalHistory');
      setLoading(false);
      if (!isIntakeProcess && persisted) {
        showSnackbar('Dental history saved successfully!', 'success');
      }
      goToNextStep();
    } catch (error) {
      setHasUnsavedChanges(true);
      setLoading(false);
      displayErrorDetails(error);
    }
  };

  const onInvalid = (e: any): void => {
    const error = Object.values(e)[0] as any;
    if (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const setupValues = (data: DentalHistory): void => {
    setObjectId(data.id ? data.id.toString() : '');
    setValue('last_dental_visit', data.last_dental_visit ?? '', { shouldValidate: true });
    setValue('who_did_you_see', data.who_did_you_see ?? '', { shouldValidate: true });
    setValue('visit_type', data.visit_type ?? '', { shouldValidate: true });

    const snapshot = {
      last_dental_visit: data.last_dental_visit ?? '',
      who_did_you_see: data.who_did_you_see ?? '',
      visit_type: data.visit_type ?? '',
    };

    setInitialValues(snapshot);
    reset(snapshot);
  };

  useEffect(() => {
    let isActive = true;

    const fetchDentalHistory = async (): Promise<void> => {
      if (!selectedPatientId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const savedDraft = sessionStorage.getItem('dentalHistory');
        if (savedDraft) {
          const parsedData = JSON.parse(savedDraft);
          if (isActive) setupValues(parsedData);
        } else {
          const { dental_history } = await listClinicalData(selectedPatientId);
          if (!isActive) return;
          if (dental_history) {
            setupValues(dental_history);
          }
        }
      } catch (error) {
        if (isActive) displayErrorDetails(error);
      } finally {
        if (isActive) setLoading(false);
      }
    };
    fetchDentalHistory();
    setHasUnsavedChanges(true);

    return () => {
      isActive = false;
    };
  }, [reset, selectedPatientId, setValue]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={56} />
        <form className={`${styles.formContainer}`} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>When is the last time you saw a dentist in person?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    {...register('last_dental_visit')}
                    label="Last dental appointment"
                    value={
                      watch('last_dental_visit') && watch('last_dental_visit') !== 'dontKnow'
                        ? dayjs(watch('last_dental_visit'))
                        : null
                    }
                    data-testid="denta-history-last-dental-visit"
                    onChange={(newValue: Dayjs | null) => {
                      if (errors.last_dental_visit) clearErrors('last_dental_visit');
                      const formattedDate = newValue ? dayjs(newValue).format('MM/DD/YYYY') : '';
                      setValue('last_dental_visit', formattedDate, { shouldValidate: true });
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'medium',
                        className: `${styles.providerSelectVisitDateInputField} ${styles.inputWrap} fs-unmask`,
                        inputProps: {
                          readOnly: false,
                          className: `${styles.profileInputClass} fs-exclude`,
                        },
                        error: !!errors.last_dental_visit,
                        helperText: errors.last_dental_visit?.message || '',
                      },
                    }}
                  />
                </LocalizationProvider>
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('last_dental_visit') === 'dontKnow'}
                    data-testid="denta-history-last-dental-visit-check"
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        setSavedLastDentalVisit(watch('last_dental_visit') || '');
                        setValue('last_dental_visit', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('last_dental_visit', savedLastDentalVisit || '', { shouldValidate: true });
                      }
                      if (errors.last_dental_visit) clearErrors('last_dental_visit');
                    }}
                    sx={{ color: 'rgba(23, 36, 51, 0.87)', '&.Mui-checked': { color: getSecondary() } }}
                    name="not_sure"
                    value="not_sure"
                  />
                  <label className={styles.checkboxLabel} htmlFor="not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <TextField
                  {...register('who_did_you_see')}
                  value={watch('who_did_you_see') === 'dontKnow' ? '' : watch('who_did_you_see')}
                  label="Dentist Seen"
                  data-testid="denta-history-who-you-visit"
                  errors={errors.who_did_you_see || {}}
                  onChange={(val) => setValue('who_did_you_see', val.target.value, { shouldValidate: true })}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                />
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('who_did_you_see') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        setSavedWhoDidYouSee(watch('who_did_you_see') || '');
                        setValue('who_did_you_see', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('who_did_you_see', savedWhoDidYouSee || '', { shouldValidate: true });
                      }
                      if (errors.who_did_you_see) clearErrors('who_did_you_see');
                    }}
                    sx={{ color: 'rgba(23, 36, 51, 0.87)', '&.Mui-checked': { color: getSecondary() } }}
                    name="not_sure"
                    value="not_sure"
                    data-testid="not-sure-checkbox"
                  />
                  <label className={styles.checkboxLabel} htmlFor="not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <MUISelectInput
                  {...register('visit_type')}
                  data-testid="complete-profile-visit-type"
                  label="Reason for visit (optional)"
                  errors={errors.visit_type ? errors : {}}
                  name="visit_type"
                  onChange={(selectedValue: string) => {
                    if (errors.visit_type) clearErrors('visit_type');
                    setValue('visit_type', selectedValue, { shouldValidate: true });
                  }}
                  value={watch('visit_type')}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                  options={visitOptions}
                />
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button
                variant="text"
                data-testid="data-history-back"
                className={styles.clinicalDataIntroFooterBack}
                onClick={() => navigate(-1)}
              >
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                data-testid="data-history-next"
                type="button"
                disabled={loading || (!isValid && !bypassRequired)}
                onClick={() => handleSubmit(onSubmit, onInvalid)()}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepNine;
